/**
 * Settings: the dashboard's second screen.
 *
 * Thomas, Sep 9 2026: Data and methods belongs in the profile settings, not
 * on the dashboard a VP reads weekly. Later the same day: "as clean as
 * possible". So every card here is a title and its rows. A subtitle survives
 * only where the rows alone would mislead, and a footnote only where it
 * states something the reader could not infer: who changes the read-only
 * rows, and that no export carries a student.
 *
 * ── NOTHING HERE PRETENDS TO SAVE ────────────────────────────────────────
 * The organization's particulars are read-only on purpose. A field that
 * accepts a change and forgets it on reload is the exact failure this
 * surface refuses to ship. The controls that do act (export, print, and on a
 * live tenant the owner's recompute) act; Billing and Team and roles are
 * their own screens, summarised here.
 *
 * ── TWO SOURCES, ONE SCREEN (Sep 10 2026) ────────────────────────────────
 * With a session the rows come from the Worker through `liveView`: the
 * organization record, the plan on the session, the claim campaign's code,
 * the team. Without one (/demo) they are the sample's. The owner's
 * "Recompute figures now" exists only on a live tenant: it is the path to a
 * first snapshot before the hourly clock is on, and the Worker answers it
 * with its own sentences (once in ten minutes; never a zero when the student
 * index is not configured).
 */

import { useEffect, useMemo, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  SectionHead,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS } from "~/app/lib/admin-nav";
import {
  downloadServerFile,
  getCapabilities,
  refreshSnapshot,
  rotateJoinCode,
  type ServerFile,
} from "~/app/lib/api";
import { downloadCsv } from "~/app/lib/csv";
import {
  DEFAULT_FILTERS,
  deriveCohort,
  sampleTeam,
  scenarioFromSearch,
  type TeamMember,
} from "~/app/lib/demo-data";
import { asOfStamp, formatCount, formatDate, formatDateRange } from "~/app/lib/format";
import { liveTeam, liveView } from "~/app/lib/live-view";
import { bandById } from "~/app/lib/pricing";
import { MethodsPanel, type CapabilitiesState } from "~/app/panels/MethodsPanel";
import { DefinitionRows } from "~/app/panels/panel-charts";

export function AdminSettings({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  /** The signed-in person, on /admin. Their institution's data replaces the sample. */
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [rotateNotice, setRotateNotice] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<ServerFile | null>(null);
  const [fileRefusal, setFileRefusal] = useState<string | null>(null);

  // The capability manifest. A signed-in institution already holds the
  // Worker's manifest on its session; the sample has no session, so it reads
  // the same manifest from the public route.
  const [sampleCapabilities, setSampleCapabilities] = useState<CapabilitiesState>({ kind: "loading" });
  useEffect(() => {
    if (session) return;
    let cancelled = false;
    void getCapabilities().then((result) => {
      if (cancelled) return;
      setSampleCapabilities(result.ok ? { kind: "ready", manifest: result.value } : { kind: "unavailable" });
    });
    return () => {
      cancelled = true;
    };
  }, [session]);
  const capabilities: CapabilitiesState = session
    ? { kind: "ready", manifest: session.capabilities }
    : sampleCapabilities;

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const search = typeof window === "undefined" ? "" : window.location.search;
  const team: readonly TeamMember[] =
    data && session
      ? data.members
        ? liveTeam(data.members, session)
        : []
      : sampleTeam(institution);
  const invited = team.filter((m) => m.status === "Invited").length;
  const band = bandById(institution.capacityBand);

  const recompute = async () => {
    setBusy(true);
    setNotice(null);
    const result = await refreshSnapshot();
    setBusy(false);
    if (result.ok) {
      setNotice(result.value.notice ?? "Your figures were recomputed.");
      live.reload();
    } else {
      // 429 REFRESH_RECENT and 503 SOURCE_UNAVAILABLE arrive as the Worker's
      // own sentences, and both are safe to show verbatim.
      setNotice(result.error.message);
    }
  };

  // A new code. The previous one keeps resolving for thirty days (the
  // Worker's rule), so a poster already on a wall is not wasted.
  //
  // Not before a first code exists (Sep 14 2026). A card tenant starts
  // without one until P3 connects it to the student app, and the Worker
  // refuses a rotation then with 409 NOT_CONNECTED, so the control, its
  // palette command and its footnote are all left out. So is the join
  // address: for a school with no code it resolves to the bare join page.
  const issued = institution.joinCode !== "";
  const canRotate = Boolean(data && session && session.role !== "viewer" && issued);
  const rotate = async () => {
    setRotating(true);
    setRotateNotice(null);
    const result = await rotateJoinCode();
    setRotating(false);
    if (result.ok) {
      setRotateNotice(
        result.value.retiresAt === null
          ? `Your join code is now ${result.value.code}.`
          : `Your join code is now ${result.value.code}. The old one keeps working until ${formatDate(result.value.retiresAt * 1000)}.`,
      );
      live.reload();
    } else {
      setRotateNotice(result.error.message);
    }
  };

  // The Worker's own two files (Sep 19 2026), on a live tenant only: the
  // sample has no session to read them with. The audit trail is for owners
  // and admins, as its route is. A refusal shows in the refusal style; a
  // download needs no line of its own, the browser shows it.
  const canExportFiles = Boolean(data && session);
  const canReadAudit = canExportFiles && session?.role !== "viewer";
  const download = async (file: ServerFile) => {
    setDownloading(file);
    setFileRefusal(null);
    const result = await downloadServerFile(file);
    setDownloading(null);
    if (!result.ok) setFileRefusal(result.error.message);
  };

  const commands: PaletteCommand[] = [
    ...(canRotate
      ? [
          {
            id: "rotate-code",
            group: "Claiming",
            label: "Rotate the join code",
            hint: "The old code works for 30 more days",
            keywords: "new code poster replace",
            run: () => void rotate(),
          },
        ]
      : []),
    {
      id: "export-csv",
      group: "Share",
      label: "Export the dashboard as a CSV",
      hint: "Aggregates only",
      keywords: "download data spreadsheet",
      run: () => downloadCsv(view),
    },
    ...(canExportFiles
      ? [
          {
            id: "export-zip",
            group: "Share",
            label: "Download every figure, with methods",
            hint: "A zip of CSV files and a README",
            keywords: "download data spreadsheet zip readme methods full export",
            run: () => void download("export"),
          },
        ]
      : []),
    ...(canReadAudit
      ? [
          {
            id: "export-audit",
            group: "Share",
            label: "Download the audit trail",
            hint: "Your team's sign-ins, invitations, reads and exports",
            keywords: "audit log trail security download csv",
            run: () => void download("audit"),
          },
        ]
      : []),
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
    ...(data && session?.role === "owner"
      ? [
          {
            id: "recompute",
            group: "Figures",
            label: "Recompute figures now",
            hint: "Once every ten minutes",
            keywords: "refresh snapshot update numbers",
            run: () => void recompute(),
          },
        ]
      : []),
  ];

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="settings"
      isSample={!data}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead
          level="page"
          id="settings"
          title="Settings"
          lede="Your organization, your account, and where every number comes from."
        />


        <div className="e-grid">
          <Card span={2}>
            <CardHeader>
              <CardTitle>Organization</CardTitle>
            </CardHeader>
            <CardBody>
              <DefinitionRows
                rows={[
                  { label: "Legal name", value: <span className="e-ui">{institution.legalName}</span> },
                  { label: "Short name", value: <span className="e-ui">{institution.displayName}</span> },
                  { label: "Location", value: <span className="e-ui">{institution.location}</span> },
                  { label: "Team email domain", value: <span className="e-ui">{institution.teamDomain}</span> },
                  {
                    label: "Enrollment estimate",
                    value: (
                      <span className="e-num">
                        {institution.enrollmentEstimate === null
                          ? "Not set"
                          : formatCount(institution.enrollmentEstimate)}
                      </span>
                    ),
                  },
                  {
                    label: "Term",
                    value: (
                      <span className="e-ui">
                        {institution.term.name},{" "}
                        {formatDateRange(institution.term.start, institution.term.end)}
                      </span>
                    ),
                  },
                ]}
              />
            </CardBody>
            <CardFootnote>
              <p className="e-note">
                Your P3 contact changes these. The enrollment estimate is the
                denominator on Roster coverage.
              </p>
            </CardFootnote>
          </Card>

          <Card solo>
            <CardHeader>
              <CardTitle>Your account</CardTitle>
            </CardHeader>
            <CardBody>
              <DefinitionRows
                rows={[
                  { label: "Access", value: <span className="e-ui">{institution.viewerRole}</span> },
                  { label: "Institution", value: <span className="e-ui">{institution.displayName}</span> },
                  { label: "Sign-in", value: <span className="e-ui">Email code</span> },
                ]}
              />
            </CardBody>
          </Card>
        </div>

        <div className="e-grid">
          <Card>
            <CardHeader>
              <CardTitle>Join code</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="e-vstack">
                <p className="e-joincode">{issued ? institution.joinCode : "Not issued"}</p>
                {issued ? <p className="e-ui">{institution.joinUrl}</p> : null}
                {canRotate ? (
                  <div className="e-row">
                    <Button variant="secondary" size="sm" onClick={() => void rotate()} disabled={rotating}>
                      {rotating ? "Rotating" : "Rotate the code"}
                    </Button>
                  </div>
                ) : null}
                {rotateNotice ? (
                  <p className="e-note" role="status">
                    {rotateNotice}
                  </p>
                ) : null}
              </div>
            </CardBody>
            {canRotate ? (
              <CardFootnote>
                <p className="e-note">A rotated code keeps working for 30 days.</p>
              </CardFootnote>
            ) : null}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Team and roles</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="e-vstack">
                <p className="e-body">
                  {data && data.members === null
                    ? "The team could not be loaded."
                    : `${formatCount(team.length)} ${team.length === 1 ? "member" : "members"}${invited > 0 ? `, ${formatCount(invited)} invited` : ""}.`}
                </p>
                <a className="e-ui" href={`${basePath}/team${search}`}>
                  Open Team and roles
                </a>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="e-vstack">
                <p className="e-body">
                  {band.name}, {institution.planInterval.toLowerCase()}, renews{" "}
                  {formatDate(institution.renewsAt)}.
                </p>
                <a className="e-ui" href={`${basePath}/billing${search}`}>
                  Open Billing
                </a>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="e-grid">
          {/* A live tenant's figures: when they were computed, and for an
              owner the button that computes them now. Not on the sample,
              whose figures are a constant. */}
          {data && session ? (
            <Card span={2}>
              <CardHeader>
                <CardTitle>Figures</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="e-vstack">
                  <DefinitionRows
                    rows={[
                      {
                        label: "Computed",
                        value: (
                          <span className="e-ui">
                            {data.metrics ? asOfStamp(view.asOf, { prefix: "" }).trim() : "Not yet"}
                          </span>
                        ),
                      },
                      {
                        label: "Students in the read",
                        value: (
                          <span className="e-ui">
                            {data.metrics ? "Every student record naming your institution" : "None yet"}
                          </span>
                        ),
                      },
                    ]}
                  />
                  {session.role === "owner" ? (
                    <div className="e-row">
                      <Button variant="secondary" size="sm" onClick={() => void recompute()} disabled={busy}>
                        {busy ? "Recomputing" : "Recompute figures now"}
                      </Button>
                    </div>
                  ) : null}
                  {notice ? (
                    <p className="e-note" role="status">
                      {notice}
                    </p>
                  ) : null}
                </div>
              </CardBody>
              <CardFootnote>
                <p className="e-note">
                  {session.role === "owner"
                    ? "Once every ten minutes. Every figure is held under ten students before it is stored."
                    : "An owner can recompute them. Every figure is held under ten students before it is stored."}
                </p>
              </CardFootnote>
            </Card>
          ) : null}

          <Card span={data ? undefined : "full"} solo={Boolean(data)}>
            <CardHeader>
              <CardTitle>Export</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="e-row">
                <Button variant="secondary" size="sm" onClick={() => downloadCsv(view)}>
                  Export CSV
                </Button>
                {canExportFiles ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void download("export")}
                    disabled={downloading !== null}
                  >
                    {downloading === "export" ? "Preparing" : "Export with methods (.zip)"}
                  </Button>
                ) : null}
                {canReadAudit ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void download("audit")}
                    disabled={downloading !== null}
                  >
                    {downloading === "audit" ? "Preparing" : "Audit trail (CSV)"}
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => window.print()}>
                  Print or save as PDF
                </Button>
              </div>
              {fileRefusal ? (
                <p className="e-field-error" role="alert">
                  {fileRefusal}
                </p>
              ) : null}
            </CardBody>
            <CardFootnote>
              <p className="e-note">
                {canReadAudit
                  ? "No export contains a student row. The zip adds a README on how each figure is counted; the audit trail is your team's sign-ins, invitations, reads and exports."
                  : canExportFiles
                    ? "No export contains a student row. The zip adds a README on how each figure is counted."
                    : "Aggregates only. No export contains a student row."}
              </p>
            </CardFootnote>
          </Card>
        </div>

        {/* Data and methods: one card of folded rows (Thomas, Sep 9 2026); the
            sixth is the capability manifest (Sep 12 2026). */}
        <MethodsPanel view={view} capabilities={capabilities} />
      </section>
    </AdminShell>
  );
}
